import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { notificationsService, type Notification } from '../services/notificationsService';

type InboxFilter = 'unread' | 'all' | 'mentions';

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [filter, setFilter] = useState<InboxFilter>('unread');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [actionLoading, setActionLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await notificationsService.list(false);
    if (error) {
      toast.error('Failed to load notifications');
      setItems([]);
    } else {
      setItems(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items]);
  const mentionCount = useMemo(() => items.filter((n) => n.type === 'mention').length, [items]);

  const filteredItems = useMemo(() => {
    if (filter === 'unread') return items.filter((n) => !n.read);
    if (filter === 'mentions') return items.filter((n) => n.type === 'mention');
    return items;
  }, [items, filter]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const setAllSelected = (checked: boolean) => {
    if (!checked) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(filteredItems.map((n) => n.id));
  };

  const onOpen = async (n: Notification) => {
    try {
      if (!n.read) {
        const { error } = await notificationsService.markAsRead(n.id);
        if (error) throw new Error(error);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      }
      setSelectedIds([]);
      if (n.link) navigate(n.link);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to open notification');
    }
  };

  const onMarkAll = async () => {
    setActionLoading(true);
    try {
      const { error } = await notificationsService.markAllAsRead();
      if (error) throw new Error(error);
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setSelectedIds([]);
      toast.success('All notifications marked as read');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to mark all as read');
    } finally {
      setActionLoading(false);
    }
  };

  const onMarkSelectedRead = async () => {
    const idsToMark = selectedIds.filter((id) => {
      const n = items.find((x) => x.id === id);
      return n && !n.read;
    });
    if (idsToMark.length === 0) return;

    setActionLoading(true);
    try {
      const { error } = await notificationsService.markManyAsRead(idsToMark);
      if (error) throw new Error(error);
      setItems((prev) => prev.map((n) => (idsToMark.includes(n.id) ? { ...n, read: true } : n)));
      toast.success('Selected marked as read');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to mark selected as read');
    } finally {
      setActionLoading(false);
    }
  };

  const onDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm('Delete selected notifications?')) return;

    setActionLoading(true);
    try {
      const { error } = await notificationsService.deleteMany(selectedIds);
      if (error) throw new Error(error);
      setItems((prev) => prev.filter((n) => !selectedIds.includes(n.id)));
      setSelectedIds([]);
      toast.success('Deleted');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete selected');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Inbox</h1>
            <div className="mt-1 text-sm text-gray-600">
              {unreadCount} unread
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => void onMarkAll()}
              disabled={actionLoading || items.length === 0 || unreadCount === 0}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Mark all read
            </button>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setFilter('unread')}
            className={`rounded-full px-3 py-1 text-sm font-medium border ${
              filter === 'unread' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Unread ({unreadCount})
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`rounded-full px-3 py-1 text-sm font-medium border ${
              filter === 'all' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            All ({items.length})
          </button>
          <button
            onClick={() => setFilter('mentions')}
            className={`rounded-full px-3 py-1 text-sm font-medium border ${
              filter === 'mentions' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Mentions ({mentionCount})
          </button>

          <div className="flex-1" />

          <button
            onClick={() => void onMarkSelectedRead()}
            disabled={actionLoading || selectedIds.length === 0}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Mark selected read
          </button>
          <button
            onClick={() => void onDeleteSelected()}
            disabled={actionLoading || selectedIds.length === 0}
            className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            Delete
          </button>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white">
          {loading ? (
            <div className="px-6 py-10 text-sm text-gray-600">Loading…</div>
          ) : filteredItems.length === 0 ? (
            <div className="px-6 py-10 text-sm text-gray-600">No notifications yet.</div>
          ) : (
            <div className="divide-y divide-gray-200">
              <div className="flex items-center gap-3 px-6 py-3 bg-gray-50">
                <input
                  type="checkbox"
                  checked={filteredItems.length > 0 && selectedIds.length === filteredItems.length}
                  onChange={(e) => setAllSelected(e.target.checked)}
                />
                <div className="text-sm text-gray-600">
                  {selectedIds.length > 0 ? `${selectedIds.length} selected` : 'Select'}
                </div>
              </div>

              {filteredItems.map((n) => (
                <div
                  key={n.id}
                  className={`w-full px-6 py-4 text-left hover:bg-gray-50 ${!n.read ? 'bg-blue-50' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={selectedSet.has(n.id)}
                      onChange={() => toggleSelected(n.id)}
                    />
                    <button
                      onClick={() => void onOpen(n)}
                      className="flex flex-1 items-start gap-3 text-left"
                    >
                      <div className="text-xl">{notificationsService.getNotificationIcon(n.type)}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-4">
                          <div className={`text-sm ${!n.read ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>{n.title}</div>
                          <div className="text-xs text-gray-500">{new Date(n.created_at).toLocaleString()}</div>
                        </div>
                        {n.message ? <div className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{n.message}</div> : null}
                        {n.link ? <div className="mt-2 text-xs text-blue-600">Open</div> : null}
                      </div>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
